import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Anchor,
  Modal,
  PinInput,
  Group,
  Checkbox,
  Divider,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Link, useNavigate } from 'react-router-dom';
import { IconBrandFacebook } from '@tabler/icons-react';
import { Layout } from '../../shared/components';
import { useAuth } from './contexts/AuthContext';
import { validatePassword } from '../../utils/authErrors';
import type { SignUpFormValues, ConfirmSignUpFormValues } from '../../types/auth';
import { useSEO } from '../../shared/hooks';
import { trackSignupStarted } from '../../utils/analytics';

export function Signup() {
  useSEO({ title: 'Rejestracja', noindex: true });
  const navigate = useNavigate();
  const { signUp, confirmSignUp, signInWithFacebook, loading } = useAuth();
  const [verificationModalOpened, setVerificationModalOpened] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Track signup form view
  useEffect(() => {
    trackSignupStarted('email');
  }, []);

  const signupForm = useForm<SignUpFormValues>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Nieprawidłowy adres email'),
      password: (value) => {
        const validation = validatePassword(value);
        return validation.valid ? null : validation.message;
      },
      confirmPassword: (value, values) =>
        value !== values.password ? 'Hasła nie są identyczne' : null,
      termsAccepted: (value) =>
        value === true ? null : 'Musisz zaakceptować regulamin i politykę prywatności',
    },
  });

  const verificationForm = useForm<ConfirmSignUpFormValues>({
    initialValues: {
      code: '',
    },
    validate: {
      code: (value) => (value.length === 6 ? null : 'Kod musi składać się z 6 cyfr'),
    },
  });

  const handleSignup = async (values: SignUpFormValues) => {
    try {
      // Generate T&C acceptance timestamp (ISO 8601 format)
      const termsAcceptedAt = new Date().toISOString();

      await signUp(values.email, values.password, termsAcceptedAt);

      // Store email for verification
      setUserEmail(values.email);

      // Show success notification
      notifications.show({
        title: 'Rejestracja zakończona!',
        message: 'Sprawdź swoją skrzynkę email i wprowadź kod weryfikacyjny',
        color: 'green',
      });

      // Open verification modal
      setVerificationModalOpened(true);
    } catch (error) {
      notifications.show({
        title: 'Błąd rejestracji',
        message: error instanceof Error ? error.message : 'Nie udało się zarejestrować',
        color: 'red',
      });
    }
  };

  const handleVerification = async (values: ConfirmSignUpFormValues) => {
    try {
      await confirmSignUp(userEmail, values.code);

      // Show success notification
      notifications.show({
        title: 'Email zweryfikowany!',
        message: 'Możesz się teraz zalogować',
        color: 'green',
      });

      // Close modal and navigate to login
      setVerificationModalOpened(false);
      void navigate('/login');
    } catch (error) {
      notifications.show({
        title: 'Błąd weryfikacji',
        message: error instanceof Error ? error.message : 'Nieprawidłowy kod weryfikacyjny',
        color: 'red',
      });
    }
  };

  const handleFacebookSignup = async () => {
    try {
      trackSignupStarted('facebook');
      await signInWithFacebook();
      // User will be redirected to Facebook OAuth
      // On callback, they'll be logged in automatically (signup via Facebook doesn't require email verification)
    } catch (error) {
      notifications.show({
        title: 'Błąd rejestracji przez Facebook',
        message: error instanceof Error ? error.message : 'Nie udało się zarejestrować',
        color: 'red',
      });
    }
  };

  return (
    <Layout isAuthenticated={false}>
      <Container size="xs" py={80}>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Title order={2} ta="center" mb="md">
            Utwórz konto
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Dołącz do Taryfy Prądu i zacznij oszczędzać
          </Text>

          {/* Facebook Signup Button */}
          <Button
            fullWidth
            leftSection={<IconBrandFacebook size={20} />}
            variant="filled"
            color="#1877F2"
            onClick={handleFacebookSignup}
            loading={loading}
            mb="md"
          >
            Zarejestruj się przez Facebook
          </Button>

          <Divider label="lub" labelPosition="center" my="lg" />

          <form onSubmit={signupForm.onSubmit(handleSignup)}>
            <Stack gap="md">
              <TextInput
                label="Adres email"
                placeholder="twoj@email.pl"
                required
                {...signupForm.getInputProps('email')}
              />

              <PasswordInput
                label="Hasło"
                placeholder="Min. 8 znaków, wielkie i małe litery, cyfry, znaki specjalne"
                required
                description="Hasło musi zawierać co najmniej 8 znaków, w tym wielkie i małe litery, cyfry oraz znaki specjalne"
                {...signupForm.getInputProps('password')}
              />

              <PasswordInput
                label="Potwierdź hasło"
                placeholder="Powtórz hasło"
                required
                {...signupForm.getInputProps('confirmPassword')}
              />

              <Checkbox
                label={
                  <Text size="sm">
                    Akceptuję{' '}
                    <Anchor component={Link} to="/terms" target="_blank" rel="noopener noreferrer">
                      Regulamin
                    </Anchor>{' '}
                    oraz{' '}
                    <Anchor component={Link} to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                      Politykę Prywatności
                    </Anchor>
                  </Text>
                }
                {...signupForm.getInputProps('termsAccepted', { type: 'checkbox' })}
              />

              <Button type="submit" fullWidth mt="md" loading={loading}>
                Załóż konto
              </Button>
            </Stack>
          </form>

          <Text c="dimmed" size="sm" ta="center" mt="xl">
            Masz już konto?{' '}
            <Anchor component={Link} to="/login" fw={600}>
              Zaloguj się
            </Anchor>
          </Text>
        </Paper>
      </Container>

      {/* Email Verification Modal */}
      <Modal
        opened={verificationModalOpened}
        onClose={() => setVerificationModalOpened(false)}
        title="Weryfikacja adresu email"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Wysłaliśmy kod weryfikacyjny na adres <strong>{userEmail}</strong>. Wprowadź
            6-cyfrowy kod poniżej.
          </Text>

          <form onSubmit={verificationForm.onSubmit(handleVerification)}>
            <Stack gap="md">
              <Group justify="center">
                <PinInput
                  length={6}
                  type="number"
                  {...verificationForm.getInputProps('code')}
                />
              </Group>

              <Button type="submit" fullWidth loading={loading}>
                Zweryfikuj email
              </Button>
            </Stack>
          </form>

          <Text size="xs" c="dimmed" ta="center">
            Nie otrzymałeś kodu? Sprawdź folder spam lub{' '}
            <Anchor
              component="button"
              type="button"
              onClick={() => {
                notifications.show({
                  title: 'Kod wysłany ponownie',
                  message: 'Sprawdź swoją skrzynkę email',
                  color: 'blue',
                });
              }}
            >
              wyślij ponownie
            </Anchor>
          </Text>
        </Stack>
      </Modal>
    </Layout>
  );
}
